import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

const faqs = [
  {
    question: "How much does a chatbot cost?",
    answer:
      "Chatbot pricing depends on complexity, integrations, and channels. Basic FAQ bots start from affordable packages, while enterprise AI solutions with CRM integration and custom training are quoted based on your requirements. Contact us for a free estimate.",
  },
  {
    question: "How long does development take?",
    answer:
      "A standard chatbot can be deployed in 2–4 weeks. Complex enterprise bots with multi-channel support, CRM integration, and custom AI training typically take 6–10 weeks. We follow an agile process with regular milestones.",
  },
  {
    question: "Can it integrate with WhatsApp?",
    answer:
      "Yes. We build WhatsApp Business API chatbots that handle customer queries, order updates, lead capture, and automated notifications — fully compliant with Meta's messaging policies.",
  },
  {
    question: "Does it support multiple languages?",
    answer:
      "Absolutely. Our chatbots support 50+ languages with automatic language detection, allowing you to serve global audiences from a single deployment.",
  },
  {
    question: "Can it connect with our CRM?",
    answer:
      "Yes. We integrate with HubSpot, Salesforce, Zoho, and custom CRMs via API. Leads, conversations, and customer data sync automatically in real time.",
  },
  {
    question: "Do you provide maintenance?",
    answer:
      "We offer ongoing maintenance, performance monitoring, model updates, and feature enhancements. Our dedicated support team ensures your chatbot stays accurate and up to date.",
  },
];

const ChatBotFaq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <AnimatedSection as="section" className="cbot-section" aria-labelledby="cbot-faq-title">
      <div className="cbot-container cbot-faq__wrap">
        <SectionHeader
          eyebrow="FAQ"
          title="Frequently Asked Questions"
          text="Everything you need to know about our AI chatbot development services."
        />

        <div className="cbot-faq__list">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={faq.question}
                className={`cbot-faq__item${isOpen ? " cbot-faq__item--open" : ""}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <button
                  type="button"
                  className="cbot-faq__trigger"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={20} aria-hidden="true" />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      className="cbot-faq__answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </AnimatedSection>
  );
};

export default ChatBotFaq;
